import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { paisa, type Money } from '@fleet/shared';
import {
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
  useUpdateVehicleMutation,
  useVehicleQuery,
} from '@/api/endpoints';
import type {
  Fuel,
  Ownership,
  Transmission,
  TravelRange,
  VehicleCategory,
  VehicleStatus,
} from '@/api/types';
import { Button, LinkButton } from '@/design/Button';
import { Confirm } from '@/design/Confirm';
import { Field, FieldRow, MoneyInput, NumberInput, SelectInput, TextInput } from '@/design/Field';
import { ErrorState } from '@/design/EmptyState';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import {
  CATEGORY_LABELS,
  FUEL_LABELS,
  OWNERSHIP_LABELS,
  TRANSMISSION_LABELS,
  TRAVEL_RANGE_LABELS,
  VEHICLE_STATUS_LABELS,
  optionsFrom,
} from './labels';

/**
 * Adding or editing a vehicle.
 *
 * Ordered the way someone reads a bluebook: plate, then what the vehicle is,
 * then who owns it, then what the website should say. The partner name only
 * appears when ownership is partner — there is no point asking whose car it is
 * when it is yours.
 *
 * Commission was removed at the client's request: they do not yet know their
 * arrangement with partners, and a percentage invented on our side would show
 * up as arithmetic nobody asked for in a revenue report.
 */

interface FormState {
  plate: string;
  make: string;
  model: string;
  year: number | null;
  category: VehicleCategory;
  seats: number | null;
  transmission: Transmission;
  fuel: Fuel;
  colour: string;
  ownership: Ownership;
  partner_name: string;
  travel_range: TravelRange;
  status: VehicleStatus;
  odometer: number | null;
  public_description: string;
  indicative_rate: Money;
}

const BLANK: FormState = {
  plate: '',
  make: '',
  model: '',
  year: null,
  category: 'sedan',
  seats: 5,
  transmission: 'manual',
  fuel: 'petrol',
  colour: '',
  ownership: 'owned',
  partner_name: '',
  travel_range: 'anywhere',
  status: 'available',
  odometer: null,
  public_description: '',
  indicative_rate: paisa(0),
};

export function VehicleFormPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== undefined && id !== 'new';
  const navigate = useNavigate();

  const existing = useVehicleQuery(id ?? '', { skip: !isEditing });
  const [createVehicle, creating] = useCreateVehicleMutation();
  const [updateVehicle, updating] = useUpdateVehicleMutation();
  const [deleteVehicle, removing] = useDeleteVehicleMutation();
  const toast = useToast();

  const [form, setForm] = useState<FormState>(BLANK);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    const vehicle = existing.data;
    if (vehicle === undefined) return;
    setForm({
      plate: vehicle.plate,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year ?? null,
      category: vehicle.category,
      seats: vehicle.seats,
      transmission: vehicle.transmission ?? 'manual',
      fuel: vehicle.fuel ?? 'petrol',
      colour: vehicle.colour ?? '',
      ownership: vehicle.ownership ?? 'owned',
      partner_name: vehicle.partner_name ?? '',
      travel_range: vehicle.travel_range ?? 'anywhere',
      status: vehicle.status ?? 'available',
      odometer: vehicle.odometer ?? 0,
      public_description: vehicle.public_description ?? '',
      indicative_rate: paisa(vehicle.indicative_rate ?? 0),
    });
  }, [existing.data]);

  const saving = creating.isLoading || updating.isLoading;
  const problems = readApiError(creating.error ?? updating.error ?? removing.error);

  async function remove(): Promise<void> {
    if (!isEditing) return;
    try {
      await deleteVehicle(id).unwrap();
      toast.done(`${form.plate} removed from the fleet.`);
      navigate('/fleet');
    } catch {
      // The server's sentence — usually "it has bookings against it" — is
      // shown on the form. Closing the dialog is what puts it in view.
      setConfirmingDelete(false);
      toast.problem('Not deleted. The reason is on the form.');
    }
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function save(): Promise<void> {
    const body = {
      plate: form.plate,
      make: form.make,
      model: form.model,
      year: form.year,
      category: form.category,
      seats: form.seats ?? 1,
      transmission: form.transmission,
      fuel: form.fuel,
      colour: form.colour,
      ownership: form.ownership,
      partner_name: form.ownership === 'partner' ? form.partner_name : '',
      travel_range: form.travel_range,
      status: form.status,
      odometer: form.odometer ?? 0,
      public_description: form.public_description,
      indicative_rate: form.indicative_rate,
    };

    const result = isEditing
      ? await updateVehicle({ id, body }).unwrap()
      : await createVehicle(body).unwrap();

    // Straight back to the list: the next vehicle is usually the point.
    navigate('/fleet', { state: { saved: result.plate } });
  }

  if (isEditing && existing.isError) {
    return (
      <ErrorState
        headline="This vehicle did not load"
        detail="It may have been removed, or the server did not answer. Go back to the fleet and open it again."
        onRetry={() => void existing.refetch()}
        retryLabel="Load again"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">{isEditing ? 'Edit vehicle' : 'Add vehicle'}</h1>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="flex flex-col gap-4">
          <Panel title="The vehicle" note="What the bluebook says" current>
            <PanelBody>
              <div className="flex flex-col gap-4">
                <FieldRow>
                  <Field
                    label="Plate"
                    required
                    error={problems.fields['plate']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        identifier
                        autoFocus
                        value={form.plate}
                        onChange={(value) => set('plate', value)}
                      />
                    )}
                  </Field>
                  <Field label="Category" required error={problems.fields['category']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.category}
                        onChange={(value) => set('category', value)}
                        options={optionsFrom(CATEGORY_LABELS)}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  {/* "Car brand", not "Make". The field behind it is still
                      `make` — that is the standard term for the column and it
                      is what the CSV the client imports is headed with — but
                      nobody at this counter says it, so nobody should have to
                      read it. Toyota is a brand; "make" is a word from a form. */}
                  <Field label="Car brand" required error={problems.fields['make']}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.make}
                        onChange={(value) => set('make', value)}
                      />
                    )}
                  </Field>
                  <Field label="Model" required error={problems.fields['model']}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.model}
                        onChange={(value) => set('model', value)}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Year" error={problems.fields['year']}>
                    {({ id: fieldId, describedBy }) => (
                      <NumberInput
                        id={fieldId}
                        describedBy={describedBy}
                        min={1980}
                        value={form.year}
                        onChange={(value) => set('year', value)}
                      />
                    )}
                  </Field>
                  <Field label="Seats" required error={problems.fields['seats']}>
                    {({ id: fieldId, describedBy }) => (
                      <NumberInput
                        id={fieldId}
                        describedBy={describedBy}
                        min={1}
                        unit="seats"
                        value={form.seats}
                        onChange={(value) => set('seats', value)}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Transmission" error={problems.fields['transmission']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.transmission}
                        onChange={(value) => set('transmission', value)}
                        options={optionsFrom(TRANSMISSION_LABELS)}
                      />
                    )}
                  </Field>
                  <Field label="Fuel" error={problems.fields['fuel']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.fuel}
                        onChange={(value) => set('fuel', value)}
                        options={optionsFrom(FUEL_LABELS)}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field label="Colour" error={problems.fields['colour']}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.colour}
                        onChange={(value) => set('colour', value)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Odometer"
                    error={problems.fields['odometer']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <NumberInput
                        id={fieldId}
                        describedBy={describedBy}
                        unit="km"
                        value={form.odometer}
                        onChange={(value) => set('odometer', value)}
                      />
                    )}
                  </Field>
                </FieldRow>
              </div>
            </PanelBody>
          </Panel>

          <Panel title="Ownership" note="Who is paid for each trip">
            <PanelBody>
              <div className="flex flex-col gap-4">
                <FieldRow>
                  <Field label="Owned by" error={problems.fields['ownership']}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.ownership}
                        onChange={(value) => set('ownership', value)}
                        options={optionsFrom(OWNERSHIP_LABELS)}
                      />
                    )}
                  </Field>
                  <Field
                    label="Status"
                    error={problems.fields['status']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.status}
                        onChange={(value) => set('status', value)}
                        options={optionsFrom(VEHICLE_STATUS_LABELS)}
                      />
                    )}
                  </Field>
                </FieldRow>

                <FieldRow>
                  <Field
                    label="Where it can go"
                    error={problems.fields['travel_range']}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={form.travel_range}
                        onChange={(value) => set('travel_range', value)}
                        options={optionsFrom(TRAVEL_RANGE_LABELS)}
                      />
                    )}
                  </Field>
                </FieldRow>

                {/* Only asked for when it applies. */}
                {form.ownership === 'partner' && (
                  <FieldRow>
                    <Field
                      label="Partner's name"
                      required
                      error={problems.fields['partner_name']}
                    >
                      {({ id: fieldId, describedBy }) => (
                        <TextInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={form.partner_name}
                          onChange={(value) => set('partner_name', value)}
                        />
                      )}
                    </Field>
                  </FieldRow>
                )}
              </div>
            </PanelBody>
          </Panel>

          <Panel title="On the website" note="What a customer sees. Publishing is a separate step.">
            <PanelBody>
              <div className="flex flex-col gap-4">
                <Field
                  label="From price"
                  error={problems.fields['indicative_rate']}
                >
                  {({ id: fieldId, describedBy }) => (
                    <MoneyInput
                      id={fieldId}
                      describedBy={describedBy}
                      value={form.indicative_rate}
                      onChange={(value) => set('indicative_rate', value)}
                    />
                  )}
                </Field>
                <Field
                  label="Description"
                  error={problems.fields['public_description']}
                >
                  {({ id: fieldId, describedBy }) => (
                    <textarea
                      id={fieldId}
                      aria-describedby={describedBy}
                      rows={3}
                      value={form.public_description}
                      onChange={(event) => set('public_description', event.target.value)}
                      className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
                    />
                  )}
                </Field>
              </div>
            </PanelBody>
          </Panel>

          {problems.detail !== undefined && (
            <p
              className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
              role="alert"
            >
              {problems.detail}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {/* A plate is the one thing a vehicle cannot be saved without:
                it is how every conversation about the car starts. */}
            <Button
              intent="primary"
              size="touch"
              type="submit"
              disabled={saving || form.plate.trim() === ''}
              title={form.plate.trim() === '' ? 'Enter the plate first.' : undefined}
            >
              {saving ? 'Saving' : isEditing ? 'Save changes' : 'Add vehicle'}
            </Button>
            <LinkButton to="/fleet" size="touch">
              Cancel
            </LinkButton>

            {/* Pushed to the far end, away from Save. The case this exists for
                is a plate typed wrong five minutes ago; a vehicle that has
                been on a booking is refused by the server, because deleting it
                would take the vehicle off invoices customers are holding. */}
            {isEditing && (
              <Button
                intent="danger"
                size="touch"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving || removing.isLoading}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </form>

      <Confirm
        open={confirmingDelete}
        title={`Delete ${form.plate || 'this vehicle'}?`}
        detail={
          <>
            <p>
              It disappears from the fleet, the calendar and every rate card that names it. This
              cannot be undone.
            </p>
            <p className="mt-2 text-ink-muted">
              A vehicle with bookings behind it will be refused — set it to{' '}
              <span className="font-medium">Not available</span> instead, which takes it out of
              everything the counter uses and keeps the history.
            </p>
          </>
        }
        confirmLabel="Delete vehicle"
        tone="alert"
        busy={removing.isLoading}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}
